import { cn, NoddiIcon } from 'noddi-ui-common';
import { CountryCode, LanguageCode, LanguageCodeType } from 'noddi-util';
import { useEffect, useRef, useState } from 'react';
import { defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Button } from '../../base/button/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../base/command';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Popover, PopoverContent, PopoverTrigger } from '../Overlay/NoddiPopover';
import { NoddiInput } from './NoddiInput';

export interface NoddiPhoneInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'size'> {
  value: string;
  onChange: (phone: string) => void;
  language?: LanguageCodeType;
  label?: string;
  disableAutoFocus?: boolean;
  /** `large`: a tall field with large digits — for a page whose only task is entering the number. */
  size?: 'default' | 'large';
}

export const NoddiPhoneInput = ({
  value,
  onChange,
  language,
  label,
  disableAutoFocus,
  size = 'default',
  'aria-label': ariaLabel,
  ...rest
}: NoddiPhoneInputProps) => {
  const isLarge = size === 'large';
  const [open, setOpen] = useState(false);

  const getDefaultCountryCode = () => {
    switch (language) {
      case LanguageCode.ENGLISH:
      case LanguageCode.NORWEGIAN:
        return CountryCode.NORWAY;
      case LanguageCode.SWEDISH:
        return CountryCode.SWEDEN;
      default:
        return CountryCode.NORWAY;
    }
  };
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: getDefaultCountryCode().toLowerCase(),
    value,
    countries: defaultCountries,
    onChange: ({ phone, inputValue: rawInputValue }) => {
      // The phone library normalizes on mount; unchanged values must not mark forms touched.
      const cleaned = (rawInputValue ?? '').trim();
      const nextValue = cleaned === '' ? '' : phone;
      if (nextValue !== value) {
        onChange(nextValue);
      }
    },
    disableDialCodeAndPrefix: true
  });
  const isMobile = useIsMobile();
  const hasMounted = useRef(false);
  const isDesktop = !isMobile;

  useEffect(() => {
    if (!disableAutoFocus && (hasMounted.current || isDesktop)) {
      //Add the timeout for proper inputRef value
      const timeoutId = setTimeout(() => {
        if (inputRef?.current) {
          inputRef?.current.focus();
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    hasMounted.current = true;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy: reason not recorded; focuses the input after country changes, and on mount on desktop, unless disableAutoFocus
  }, [country]);

  const handleCountrySelect = (selectedIso2: string) => {
    // selectedIso2 is directly the iso2 code passed from the onSelect callback
    setCountry(selectedIso2);
    setOpen(false);
  };

  // A visible label names the field. Otherwise the caller's aria-label, then the English fallback.
  const accessibleName = label ?? ariaLabel ?? 'Phone number';
  // The country list is English; the button's name follows the page language instead.
  // `se` is the app's Swedish code, but a different language to Intl.
  const regionLocale = language === LanguageCode.SWEDISH ? 'sv' : (language ?? LanguageCode.ENGLISH);
  const countryName = new Intl.DisplayNames([regionLocale], { type: 'region' }).of(country.iso2.toUpperCase());

  return (
    <NoddiInput
      aria-label={label ? undefined : accessibleName}
      // Forward common input props (e.g., name, onBlur, placeholder, disabled, etc.)
      {...rest}
      value={inputValue}
      onChange={handlePhoneValueChange}
      type='tel'
      ref={inputRef}
      className={cn(isLarge && 'h-13 rounded-lg [&_input]:text-lg [&_input]:tracking-wide')}
      labelClassName={cn(isLarge && 'font-medium')}
      wrapperClassName={cn(isLarge && 'gap-2')}
      startAdornment={
        <Popover open={open} onOpenChange={setOpen} modal>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              aria-label={`${accessibleName}, ${countryName ?? country.name} +${country.dialCode}`}
              aria-haspopup='listbox'
              aria-expanded={open}
              className={cn(
                '-mr-4 -ml-2 h-9 w-auto justify-between pr-5 pl-2 font-normal',
                'border-0 bg-transparent hover:bg-transparent',
                'focus-visible:ring-0 focus-visible:ring-offset-0'
              )}
            >
              <div className='flex items-center gap-2'>
                <FlagImage size={22} iso2={country.iso2} />
                <span>+{country.dialCode}</span>
                <NoddiIcon className='opacity-50' name='ChevronsUpDown' />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className='p-0' align='start' alignOffset={-9}>
            <Command>
              <CommandInput aria-label='Search countries' placeholder='Search countries...' className='h-9' />
              <CommandList>
                <CommandGroup>
                  {defaultCountries.map((countryData) => {
                    const { iso2, name, dialCode } = parseCountry(countryData);
                    return (
                      <CommandItem
                        key={iso2}
                        value={iso2}
                        keywords={[name, iso2, dialCode, `+${dialCode}`]}
                        onSelect={() => handleCountrySelect(iso2)}
                        aria-label={`${name} +${dialCode}${country.iso2 === iso2 ? ', selected' : ''}`}
                        className='flex items-center gap-3 py-2'
                      >
                        <FlagImage size={22} iso2={iso2} />
                        <div className='flex flex-1 items-center justify-between'>
                          <span className='text-sm'>{name}</span>
                          <span className='text-sm text-muted-foreground'>+{dialCode}</span>
                        </div>
                        <NoddiIcon className={cn(country.iso2 === iso2 ? 'opacity-100' : 'opacity-0')} name='Check' />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                <CommandEmpty>No country found.</CommandEmpty>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      }
      label={label}
    />
  );
};
